import { useId } from 'react';

import { css } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl';

import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations/index';

import { Image } from '../../../../../../libraries/design/components/image/src/image';
import { Dropdown } from '../../inputs/dropdown';
import { NumericInput } from '../../inputs/numeric-input';

import { ProductCardEditionOption } from './edition-options';

import type { Edition } from '../../../calculator/types';
import type { BulletOrIconListItem } from '../../strings';

export interface ProductCardProps {
	/** Product display name shown in the card header. */
	title: string;
	/** Optional short description shown below the title. */
	description?: string;
	/** Optional product icon URL shown next to the title. */
	iconUrl?: string;
	/** Optional list of included apps/features shown below the description. */
	list?: BulletOrIconListItem[];
	/** Optional additional benefit string shown below the list. */
	additionalBenefit?: string;
	/** Editions offered, in dropdown order, with their display labels. */
	editions: ProductCardEditionOption[];
	edition: Edition;
	onEditionChange: (edition: Edition) => void;
	/** Plural noun for the billable unit (e.g. "users", "agents"). */
	unitLabelPlural: string;
	/** Singular noun for the billable unit (e.g. "user", "agent"). */
	unitLabelSingular: string;
	unitCount: number;
	onUnitCountChange: (unitCount: number) => void;
	/** Max selectable unit count, sourced from the product config's `signupLimit`. */
	maxUnitCount: number;
	/** When true the footer shows the free-plan message instead of a price. */
	isFree: boolean;
	/** Pre-formatted per-unit/month price; `undefined` while pricing loads. */
	formattedPerUnitPerMonth?: string;
}

const EDITION_FIELD_LABEL = 'Edition';
const FREE_PLAN_MESSAGE = 'Free plan — no charge.';
const NO_PRICE_PLACEHOLDER = '—';

/**
 * Presentational product card. Owns layout and its own fixed chrome text only;
 * all variable content (product/unit labels, editions, price) is passed in, so
 * the component has no knowledge of the calculator context.
 */
export const ProductCard = (props: ProductCardProps) => {
	const {
		title,
		description,
		iconUrl,
		list,
		additionalBenefit,
		editions,
		edition,
		onEditionChange,
		unitLabelPlural,
		unitLabelSingular,
		unitCount,
		onUnitCountChange,
		maxUnitCount,
		isFree,
		formattedPerUnitPerMonth,
	} = props;

	const cardId = useId();
	const intl = useIntl();

	return (
		<div css={cardStyle} className={`icon_${iconUrl ? 'true' : 'false'}`}>
			{iconUrl && (
				<div css={iconContainer}>
					<Image src={iconUrl} alt="" css={iconImageStyle} />
				</div>
			)}
			<div css={headerRowStyle}>
				<div>
					<h3 css={panelTitleStyle}>{title}</h3>
				</div>
				{description && <p css={descriptionStyle}>{description}</p>}
			</div>

			<div css={priceLineStyle}>
				{isFree ? (
					<em>{FREE_PLAN_MESSAGE}</em>
				) : (
					<>
						<span css={priceValueStyle}>{formattedPerUnitPerMonth ?? NO_PRICE_PLACEHOLDER}</span>
						<span>Per {unitLabelSingular}/month</span>
					</>
				)}
			</div>

			{list && list.length > 0 && (
				<div>
					<p css={additionalParagraphStyle}>
						<FormattedMessage
							id="ubp.calculator.productcard.collectionincludes"
							defaultMessage="Includes {edition} licensing for the following apps:"
							values={{ edition: edition as string }}
						/>
					</p>
					<ul css={listContainerStyle}>
						{list.map((listItem: BulletOrIconListItem, index: number) => {
							return (
								<li css={listElementStyle} key={`product-card-list-${index}`}>
									{listItem.text}
								</li>
							);
						})}
					</ul>
				</div>
			)}

			{additionalBenefit && (
				<div>
					<p css={additionalParagraphStyle}>
						{intl.formatMessage({
							id: 'ubp.calculator.additionalbenefit',
							defaultMessage: 'You also receive:',
						})}
					</p>

					<ul css={listContainerStyle}>
						<li css={listElementStyle}>{additionalBenefit}</li>
					</ul>
				</div>
			)}

			<div css={inputRowStyle}>
				<Dropdown
					id={`editionDropdown-${cardId}`}
					label={EDITION_FIELD_LABEL}
					labelHidden={true}
					value={edition}
					onChange={(value) => onEditionChange(value as Edition)}
					options={editions.map((option) => ({
						value: option.value,
						label: option.label,
					}))}
				/>

				{/*
				 * Wrap NumericInput + unit label so they stay paired. On
				 * mobile viewports (< 768px) `flexBasis: 100%` on the
				 * wrapper forces the input+label pair onto its own row
				 * below the Edition dropdown — otherwise all three
				 * elements collide horizontally within the card width.
				 */}
				<div css={inputAndLabelWrapperStyle}>
					<NumericInput
						id={`users-${cardId}`}
						placeholder={'0'}
						min={0}
						max={maxUnitCount}
						variant="inline"
						width={120}
						value={unitCount}
						onChange={(next) => onUnitCountChange(next ?? 0)}
					/>
					<label htmlFor={`users-${cardId}`}>{unitLabelPlural}</label>
				</div>
			</div>
		</div>
	);
};

const cardStyle = css({
	borderRadius: wacToken('corner.radius.card'),
	position: 'relative',
	display: 'flex',
	flexDirection: 'column',
	backgroundColor: wacToken('color.neutral.light100'),
	padding: token('space.250'),
	gap: token('space.200'),
	['&.icon_true']: {
		paddingInlineStart: token('space.800'),
	},
});

const iconImageStyle = css({
	display: 'block',
	width: '32px',
	height: '32px',
	objectFit: 'contain',
});

const iconContainer = css({
	position: 'absolute',
	insetInlineStart: token('space.200'),
	insetBlockStart: token('space.300'),
});

const headerRowStyle = css({
	display: 'flex',
	flexDirection: 'column',
	gap: '0.25rem',
});

const listContainerStyle = css({
	padding: `0 0 0 ${token('space.300')}`,
	margin: 0,
});

const listElementStyle = css({
	fontSize: '0.875rem',
	fontFamily: wacToken('font.charlie.text'),
	margin: `0 0 ${token('space.050')}`,
	position: 'relative',
});

const additionalParagraphStyle = css({
	margin: 0,
	fontSize: '0.875rem',
	fontFamily: wacToken('font.charlie.text'),
});

const descriptionStyle = css({
	margin: 0,
	fontSize: '0.875rem',
	fontFamily: wacToken('font.charlie.text'),
});

const panelTitleStyle = css({
	margin: 0,
	fontSize: '1.5rem',
	fontWeight: 700,
	lineHeight: 1.25,
});

// Layout: Edition dropdown + unit input + unit label.
// On tablet+ they sit on a single row. On mobile viewports (< 768px)
// they wrap so the unit input + label drop onto their own line below
// the dropdown — otherwise the dropdown, input, and label collectively
// exceed the card width and either clip or force horizontal overflow.
const inputRowStyle = css({
	display: 'flex',
	justifyContent: 'stretch',
	alignItems: 'center',
	gap: token('space.100'),
	flexWrap: 'wrap',
});

// Wrapper for NumericInput + unit label so they stay paired. On mobile
// viewports (< 768px) forces the input+label pair onto its own row below
// the Edition dropdown via `flexBasis: 100%`. On tablet+ shrinks to fit
// content and sits inline with the dropdown.
const inputAndLabelWrapperStyle = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.100'),
	flexBasis: '100%',
	[media.above.md]: {
		flexBasis: 'auto',
	},
});

const priceLineStyle = css({
	fontFamily: wacToken('font.charlie.text'),
	fontSize: '1rem',
	display: 'flex',
	flexDirection: 'column',
});

const priceValueStyle = css({
	fontWeight: 700,
	fontSize: '1.5rem',
});
